import { CENTERCOMPONENTS } from './CenterComponents';
import { ACTIVETAB } from './RightSideComponents';
import { Shapes } from '../../Shapes';
import { Filters } from '../../Filters/Filters';
import { Reports } from '../../Reports/Reports';
import { Events } from '../../Events/Events';
import { useMapPanelContext } from './MapPanelContext';
import {
  CollapsedTable,
  ReportShipTable,
} from '../../Reports/Table/ReportShipTable';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  MapScale,
  useDrawingContext,
  useOpenLayersContext,
  useResetDrawingContext,
} from '@ais-ui/react-openlayers';
import {
  Badge,
  Button,
  PlacedToastViewport,
  Separator,
  ToastView,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToastContext,
} from '@ais-ui/ui-core';
import { DrawingMenu } from '../DrawingMenu';
import { useAISMapContext } from '../AISMapContext';
import { useReportContext } from '../../Reports/ReportContext';
import { useSession } from '@ais-ui/websockets';
import { LayerControl } from '../LayerControl/LayerControl';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { MeasureTool } from '../MeasureTool/MeasureTool';
import { ShipPortSearch } from '../Search/ShipPortSearch';
import { cn, useTimeZoneContext } from '@ais-ui/utils';
import { Replays } from '../../Replay/Replays';
import { Messages } from '../../Messages/Messages';
import { CustomizeReport } from '../../Reports/Customize/CustomizeReport';
import { FilterShips } from '../FilterTargets/FilterShips';
import { ColorTargetsBy } from '../ColorTargetsBy/ColorTargetsBy';
import { ChooseTargetCard } from '../ChooseTargetCard';
import { CollapsedReportDetails } from '../../Reports/CollapsedReportDetails';
import { TargetDetails } from '../../Targets/TargetDetails';
import { GridClasses, useGridClasses } from './useGridClasses';
import { useSettings } from '../../Settings/SettingsProvider';
import { TargetsOnMap } from '../TargetsOnMap/TargetsOnMap';
import { TargetsOnMapTable } from '../TargetsOnMap/TargetsOnMapTable';
import { PortDetails } from '../../Ports/PortDetails';
import { LegendContainer } from '../LegendContainer';
import { RoutesTable } from '../../Routes/RoutesTable';
import { useRoutes } from '../../Routes/RoutesContext';
import { PiMinus, PiMonitorPlay, PiMouse, PiPlus, PiBoat } from 'react-icons/pi';
import { useReplayContext } from '../../Replay/ReplayContext';
import { useViewMode, ViewMode } from '../../../components/ViewModeProvider';
import { ResizableTable } from './ResizableTable/ResizableTable';
import { useSelectionState } from '../../../components/SelectionStateProvider';
import { useGPSLost } from '../GpsLost/GPSLostContext';
import { MapAtons } from '../../Atons/MapAtons';
import { useLengthUnits } from '../../Units/useLengthUnits';
import { usePermissions } from '../../Permissions/PermissionsProvider';
import { useIsMobile } from '@ais-ui/ui-core';
import { VesselDetailsMobileView } from '../../../components/vessel/VesselDetailsMobileView';
import { FilterDrawer } from '../../../components/filters/FilterDrawer';
import { PiFunnel } from 'react-icons/pi';
import { useFilterContext } from '../FilterContext';
import { UserDropdown } from '../../../components/sidebar/UserDropdown/UserDropdown';

const CursorPositionBadge = () => {
  const { cursorPosition } = useAISMapContext();
  const { settings } = useSettings();

  if (!settings['ribbonPlugin.settings']?.show_cursor_position) {
    return null;
  }

  if (!cursorPosition) {
    return null;
  }

  return (
    <Badge
      variant="secondary"
      className="bg-card dark:bg-card/90 shadow-md text-sm w-fit cursor-default hover:bg-card text-card-foreground"
    >
      {cursorPosition.lat} {cursorPosition.lon}
    </Badge>
  );
};

const TargetCountBadge = () => {
  const { targetCountInfo } = useAISMapContext();
  const { settings } = useSettings();
  const permissions = usePermissions();

  // Check if the admin setting is enabled
  if (!settings.showTargetCountBadge) {
    return null;
  }

  // Check if user has admin permissions
  if (!permissions.admin) {
    return null;
  }

  // Check if we have target count info
  if (!targetCountInfo) {
    return null;
  }

  return (
    <Badge
      variant="secondary"
      className="bg-card dark:bg-card/90 shadow-md text-sm w-fit cursor-default hover:bg-card text-card-foreground"
    >
      {targetCountInfo}
    </Badge>
  );
};

const RemoteControlBadge = () => {
  const { settings } = useSettings();

  const isRemoteControlEnabled = useMemo(() => {
    return settings['ribbonPlugin.settings']?.remotecontrol_enabled ?? false;
  }, [settings]);

  if (!isRemoteControlEnabled) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger className="w-fit">
        <Badge
          variant="secondary"
          className="bg-card dark:bg-card/90 shadow-md text-sm cursor-default hover:bg-card space-x-2 text-card-foreground"
        >
          <PiMouse className="size-4" />
          <p>Remote control active</p>
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <p>
          External applications can control this view. Click Settings to
          disable.
        </p>
      </TooltipContent>
    </Tooltip>
  );
};

const MapPanelScale = () => {
  const { settings } = useSettings();

  const { selectedUnit } = useLengthUnits();

  const isMapScaleEnabled = useMemo(() => {
    return settings['ribbonPlugin.settings']?.mapscale_enabled ?? false;
  }, [settings]);

  if (!isMapScaleEnabled) {
    return null;
  }

  return <MapScale unitSystem={selectedUnit}></MapScale>;
};

const MapPanelZoomButtons = () => {
  const { settings } = useSettings();

  const { map } = useOpenLayersContext();

  const isZoomButtonsEnabled = useMemo(() => {
    return settings['ribbonPlugin.settings']?.zoom_buttons_enabled ?? false;
  }, [settings]);

  const handleZoomIn = () => {
    if (!map) {
      return;
    }
    const view = map.getView();
    const currentZoom = view.getZoom();

    if (!currentZoom) {
      return;
    }

    // Animate zoom in
    view.animate({
      zoom: currentZoom + 1,
      duration: 250,
    });
  };

  const handleZoomOut = () => {
    if (!map) {
      return;
    }
    const view = map.getView();
    const currentZoom = view.getZoom();
    if (!currentZoom) {
      return;
    }
    // Animate zoom out
    view.animate({
      zoom: currentZoom - 1,
      duration: 250,
    });
  };

  if (!isZoomButtonsEnabled) {
    return null;
  }

  return (
    <div className="flex flex-col items-center bg-background border rounded-lg shadow-lg">
      <Button
        size="icon"
        variant="ghost"
        className="size-8"
        onClick={handleZoomIn}
      >
        <PiPlus></PiPlus>
      </Button>
      <Separator className="w-4/6"></Separator>
      <Button
        size="icon"
        variant="ghost"
        className="size-8"
        onClick={handleZoomOut}
      >
        <PiMinus></PiMinus>
      </Button>
    </div>
  );
};

const CreatedAtBadge = () => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { createdAt } = useAISMapContext();

  if (!createdAt) {
    return null;
  }

  return (
    <Badge
      variant="secondary"
      className="bg-card dark:bg-card/90 shadow-md text-sm w-fit cursor-default hover:bg-card text-card-foreground"
    >
      {formatDateTimeWithTimeZone(createdAt)}
    </Badge>
  );
};

const ReplayTimeBadge = () => {
  const { currentTime } = useReplayContext();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { viewMode } = useViewMode();

  if (!currentTime || viewMode !== ViewMode.Replay) {
    return null;
  }

  return (
    <Badge
      variant="secondary"
      className="bg-card dark:bg-card/90 shadow-md text-sm w-fit cursor-default hover:bg-card space-x-2 text-card-foreground"
    >
      <PiMonitorPlay></PiMonitorPlay>
      <p>{formatDateTimeWithTimeZone(currentTime)}</p>
    </Badge>
  );
};

interface MapPanelTableProps {
  gridClasses: GridClasses;
}

const MapPanelTable = ({ gridClasses }: MapPanelTableProps) => {
  const {
    centerComponent,
    centerComponentData,
    tableExpanded,
    loadingCenterComponentData,
  } = useMapPanelContext();
  const { reportType, reportDetails } = useReportContext();

  if (!tableExpanded) {
    return null;
  }

  return (
    <ResizableTable
      className={cn(gridClasses.reportTableGrid, 'self-end cursor-default')}
    >
      {centerComponent === CENTERCOMPONENTS.SHIPTABLE && (
        <ReportShipTable
          className="w-full h-full"
          shipData={centerComponentData}
          jobId={reportDetails?.info?.['Job ID'] as string}
          reportType={reportType}
          isLoading={loadingCenterComponentData}
        />
      )}
      {centerComponent === CENTERCOMPONENTS.TARGETSONMAP && (
        <TargetsOnMapTable className="w-full h-full" />
      )}
      {centerComponent === CENTERCOMPONENTS.ROUTES && (
        <RoutesTable className="w-full h-full" />
      )}
    </ResizableTable>
  );
};

export const MapPanel = () => {
  const mapPanelRef = useRef<HTMLDivElement>(null);
  const { settings } = useSettings();
  const [isTargetCardExpanded, setIsTargetCardExpanded] = useState(
    settings['ribbonPlugin.settings'].target_card_expanded_by_default ?? true,
  );
  const isMobile = useIsMobile();

  // Mobile-specific state - must be at top level, not inside conditional
  const { 
    currentTargetInfo, 
    setTargetInfoDetailsList, 
    showVesselDetailCardMobile, 
    setShowVesselDetailCardMobile 
  } = useSelectionState();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const filterContext = useFilterContext();

  const { setToastView } = useToastContext();
  const {
    reportType,
    reportDetails,
  } = useReportContext();
  const { selectedPorts } = useSelectionState();
  const { timeZone } = useTimeZoneContext();
  const gridClasses = useGridClasses(isTargetCardExpanded);
  const { dbSession, replaySession } = useSession();
  const permissions = usePermissions();
  const { shape: routesShape } = useRoutes();

  useEffect(() => {
    // Runs on mount
    setToastView(ToastView.Map);
    // Runs on unmount
    return () => {
      setToastView(ToastView.Normal);
    };
  }, []);
  const resetDrawingContext = useResetDrawingContext();
  const { showDrawPanel } = useDrawingContext();
  const { viewMode } = useViewMode();

  // In Drawing mode, hide most UI to let user focus on the map
  const isDrawingMode = viewMode === ViewMode.Drawing;
  const isKioskMode = viewMode === ViewMode.Kiosk;
  const isMinimalUI = isDrawingMode || isKioskMode;

  const {
    activeTab,
    tabChange,
    setTabChange,
    tableExpanded,
    reportDetailsExpanded,
    centerComponent,
    setCenterComponent,
  } = useMapPanelContext();

  // We cannot reset the drawing context from the sidebar, so we have to emit the change through tabChange
  useEffect(() => {
    if (tabChange) {
      resetDrawingContext();
      setTabChange(false);
    }
  }, [resetDrawingContext, tabChange, setTabChange]);

  // Handle Routes Table cleanup when no ports or shapes are selected
  // NOTE: Routes table is now controlled by PortDetails.tsx based on portSelectionMode
  // Also keep routes table open when a shape is selected via ShowRoutesTableDropdownButton

  useEffect(() => {
    // Only clear routes table when no ports are selected AND no shape is selected
    if (selectedPorts.length === 0 && !routesShape && centerComponent === CENTERCOMPONENTS.ROUTES) {
      setCenterComponent(CENTERCOMPONENTS.NONE);
    }
  }, [selectedPorts, routesShape, centerComponent, setCenterComponent]);

  const rightSideMapPanelClass = `w-[384px] cursor-default row-start-1 row-end-4 col-start-6 col-end-7 pointer-events-auto`;

  // Mobile view: only show search bar centered at top
  if (isMobile) {
    // Show vessel details full-screen when vessel is selected AND detail card is shown
    if (currentTargetInfo && showVesselDetailCardMobile) {
      return (
        <VesselDetailsMobileView 
          onClose={() => {
            setTargetInfoDetailsList([]);
            setShowVesselDetailCardMobile(false);
          }} 
        />
      );
    }

    // Calculate active filter count for badge
    const activeFilterCount =
      filterContext.simpleFilters.length + filterContext.customFilters.length;

    return (
      <>
        {/* Top: Search bar only */}
        <div className="absolute top-4 inset-x-4 flex items-center pointer-events-none">
          <ShipPortSearch className="pointer-events-auto h-10 w-full" />
        </div>

        {/* Bottom-left: Vertical action bar with 3 buttons */}
        <div className="absolute left-4 bottom-6 flex flex-col gap-3 pointer-events-none">
          <Button
            id="mobile-target-details-button"
            variant="outline"
            size="icon"
            onClick={() => setShowVesselDetailCardMobile(true)}
            disabled={!currentTargetInfo}
            className="pointer-events-auto size-12 shrink-0 bg-background dark:bg-background shadow-md border"
          >
            <PiBoat className="size-6" />
          </Button>
          <Button
            id="mobile-filter-button"
            variant="outline"
            size="icon"
            onClick={() => setIsFilterDrawerOpen(true)}
            className="pointer-events-auto size-12 shrink-0 bg-background dark:bg-background shadow-md border relative"
          >
            <PiFunnel className="size-6" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-2 -right-2 inline-flex items-center justify-center px-1 py-0.5 text-xs font-bold leading-none text-red-100 bg-primary rounded-full min-w-[1.25rem]">
                {activeFilterCount}
              </span>
            )}
          </Button>
          <div className="pointer-events-auto">
            <UserDropdown expanded={false} mobileMode={true} />
          </div>
        </div>

        <div className="absolute bottom-6 left-20 right-4 flex justify-start pointer-events-none">
          <PlacedToastViewport className="pointer-events-auto space-y-2 max-w-xs" />
        </div>
        <FilterDrawer
          isOpen={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
        />
      </>
    );
  }

  return (
    <div
      ref={mapPanelRef}
      className={cn(
        'absolute top-4 right-4 grid w-[calc(100%-2rem)] h-[calc(100%-2rem)] bg-transparent pointer-events-none gap-2',
      )}
      style={gridClasses.mapPanelGrid}
    >
      {!isMinimalUI && (
        <ShipPortSearch className="pointer-events-auto row-start-1 col-start-1 h-10" />
      )}
      {!isMinimalUI && (
        <div
          className={cn(
            gridClasses.layerControllerGrid,
            'flex flex-col space-y-2 h-full justify-end bg-transparent w-fit pointer-events-none',
          )}
        >
          <TargetsOnMap className="pointer-events-auto dark:bg-popover"></TargetsOnMap>

          <ColorTargetsBy className="pointer-events-auto dark:bg-popover"></ColorTargetsBy>
          <FilterShips className="pointer-events-auto dark:bg-popover"></FilterShips>
          <MeasureTool className="pointer-events-auto dark:bg-popover"></MeasureTool>
          <LayerControl className="pointer-events-auto dark:bg-popover"></LayerControl>
        </div>
      )}

      <div
        className={cn(
          gridClasses.metadataGrid,
          'flex flex-col items-end bg-transparent self-end justify-self-end pointer-events-auto w-fit space-y-2 overflow-hidden',
        )}
      >
        <PlacedToastViewport className="m-0 p-0 space-y-2" />
        {!isMinimalUI && !tableExpanded && <CollapsedTable />}
        {!isMinimalUI && !reportDetailsExpanded && <CollapsedReportDetails />}
        {!isMinimalUI && (
          <CustomizeReport
            reportType={reportType}
            report={reportDetails}
            className=""
          ></CustomizeReport>
        )}
        {!isMinimalUI && !dbSession && (
          <Badge variant="destructive" className="shadow-md text-sm w-fit">
            Reports down
          </Badge>
        )}
        {!isMinimalUI && !replaySession && (
          <Badge variant="destructive" className="shadow-md text-sm w-fit">
            Replay down
          </Badge>
        )}

        {!isMinimalUI && <ChooseTargetCard></ChooseTargetCard>}
        {!isKioskMode && <MapPanelZoomButtons></MapPanelZoomButtons>}
        {!isKioskMode && timeZone && (
          <Badge
            variant="secondary"
            className="bg-card dark:bg-card/90 shadow-md text-sm w-fit cursor-default hover:bg-card text-card-foreground"
          >
            {timeZone}
          </Badge>
        )}
        {!isKioskMode && <MapPanelScale></MapPanelScale>}

        {!isMinimalUI && <CreatedAtBadge></CreatedAtBadge>}

        {!isMinimalUI && <ReplayTimeBadge></ReplayTimeBadge>}
        {!isMinimalUI && <RemoteControlBadge></RemoteControlBadge>}
        {!isKioskMode && <CursorPositionBadge></CursorPositionBadge>}
        {!isMinimalUI && <TargetCountBadge></TargetCountBadge>}
      </div>

      {!isMinimalUI && (
        <LegendContainer
          className={cn(
            gridClasses.reportLegendGrid,
            'justify-self-end cursor-default pointer-events-auto',
          )}
        />
      )}

      {/* Drawing menu - centered at bottom when active */}
      {showDrawPanel && viewMode === ViewMode.Drawing && (
        <div className="col-start-1 col-end-7 row-start-3 row-end-4 flex items-end justify-center pb-4 pointer-events-none">
          <DrawingMenu className="pointer-events-auto scale-125 origin-bottom" />
        </div>
      )}
      
      {!isMinimalUI && centerComponent !== CENTERCOMPONENTS.NONE && (
        <MapPanelTable gridClasses={gridClasses}></MapPanelTable>
      )}
      {!isMinimalUI && (
        <TargetDetails
          isExpanded={isTargetCardExpanded}
          setIsExpanded={setIsTargetCardExpanded}
          className={cn(
            gridClasses.targetInfoGrid,
            'cursor-default pointer-events-auto',
          )}
        />
      )}
      {/* PortDetails handles drawing port and opening routes table for 'routes' mode */}
      {/* It only renders the card UI for 'nextport' and 'popout' modes */}
      {!isMinimalUI && selectedPorts.length > 0 && (
        <PortDetails
          className={cn(
            gridClasses.portInfoGrid,
            'cursor-default pointer-events-auto',
          )}
        ></PortDetails>
      )}
      {/* 
        NOTE: In Drawing mode (isMinimalUI), we keep these panels mounted but hidden
        to preserve dialog state. If we unmount them, any open dialogs (like shape 
        creation dialogs) would be lost when the user finishes drawing.
      */}
      {activeTab === ACTIVETAB.ATONS && (
        <MapAtons className={cn(rightSideMapPanelClass, isMinimalUI && 'hidden')} />
      )}
      {activeTab === ACTIVETAB.EVENTS && (
        <Events className={cn(rightSideMapPanelClass, isMinimalUI && 'hidden')} />
      )}
      {activeTab === ACTIVETAB.FILTERS && (
        <Filters className={cn(rightSideMapPanelClass, isMinimalUI && 'hidden')} />
      )}
      {activeTab === ACTIVETAB.SHAPES && (
        <Shapes className={cn(rightSideMapPanelClass, isMinimalUI && 'hidden')} />
      )}
      {activeTab === ACTIVETAB.REPORTS && (
        <Reports className={cn(rightSideMapPanelClass, isMinimalUI && 'hidden')} />
      )}
      {activeTab === ACTIVETAB.REPLAY && (
        <Replays
          className={cn(
            gridClasses.replayGrid,
            'cursor-default pointer-events-auto self-end justify-self-end',
            isMinimalUI && 'hidden',
          )}
        />
      )}
      {activeTab === ACTIVETAB.MESSAGES && (
        <Messages className={cn(rightSideMapPanelClass, isMinimalUI && 'hidden')}></Messages>
      )}
    </div>
  );
};
